import { Component, input, Input, OnDestroy, OnInit, output, ViewChild } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Merchant, PaymentCard, PaymentCardTypeLabel, StripeService } from '@galaxy/billing';
import { EditContactInfoDialogComponent, EditPaymentMethodDialogComponent } from '@vendasta/billing-ui';
import { Observable, Subscription } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { BillingSettingsService } from '../shared-billing/settings/billing-settings.service';
import { BillingAction, PaymentInfo } from './checkout.service';

@Component({
  selector: 'app-instant-bill-payment-info',
  styleUrls: ['./instant-bill-payment-info.component.scss'],
  templateUrl: './instant-bill-payment-info.component.html',
  standalone: false,
})
export class InstantBillPaymentInfoComponent implements OnInit, OnDestroy {
  @ViewChild('consent') consentCheckbox: MatCheckbox;
  @Input() editable = false;
  @Input() merchantId: string;
  @Input() paymentRequired: boolean;
  billingAction = input<BillingAction>();
  reloadBillingAction = output<void>();

  paymentInfo$: Observable<PaymentInfo>;
  BillingAction = BillingAction;

  stripeKey$: Observable<string>;

  subscriptions: Subscription[] = [];

  constructor(
    private dialog: MatDialog,
    private billingSettingsService: BillingSettingsService,
    private stripeService: StripeService,
  ) {}

  ngOnInit(): void {
    this.paymentInfo$ = this.billingSettingsService.paymentDetails.pipe(
      map((paymentDetails: PaymentCard) => {
        let paymentClass = '';
        if (paymentDetails.id.length) {
          paymentClass = this.getPaymentFontClass(paymentDetails.cardType);
        }
        return {
          lastFourDigits: paymentDetails.lastFourDigits,
          paymentClass: paymentClass,
        };
      }),
    );

    this.stripeKey$ = this.stripeService.getPublicKeyByMerchant(this.merchantId);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  openEditContactDialog(): void {
    this.subscriptions.push(
      this.billingSettingsService.merchantDetailsInfo.pipe(take(1)).subscribe((contactInfo) => {
        const dialogRef = this.dialog.open(EditContactInfoDialogComponent, {
          width: '500px',
          data: {
            merchantId: this.merchantId,
            merchant: contactInfo.merchantId ? (contactInfo as Merchant) : null,
          },
        });
        dialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe((_) => {
            this.reloadBillingAction.emit();
          });
      }),
    );
  }

  openEditPaymentDialog(): void {
    if (this.consentCheckbox) {
      this.consentCheckbox.checked = false;
    }

    this.subscriptions.push(
      this.stripeKey$
        .pipe(
          withLatestFrom(this.billingSettingsService.paymentDetailsInfo),
          map(([stripeKey, card]) => {
            const dialogRef = this.dialog.open(EditPaymentMethodDialogComponent, {
              width: '500px',
              data: {
                merchantId: this.merchantId,
                stripePublicKey: stripeKey,
                paymentCard: card !== this.billingSettingsService.emptyPaymentDetails ? card : null,
              },
            });
            dialogRef
              .afterClosed()
              .pipe(take(1))
              .subscribe((merchantUpdated) => {
                if (merchantUpdated) {
                  this.billingSettingsService.loadMerchantCreditCards();
                }
                this.reloadBillingAction.emit();
              });
          }),
          take(1),
        )
        .subscribe(),
    );
  }

  getPaymentFontClass(cardType: PaymentCardTypeLabel): string {
    const cardBrandToPfClass = {
      Visa: 'pf-visa',
      Mastercard: 'pf-mastercard',
      'American Express': 'pf-american-express',
      Discover: 'pf-discover',
      'Diners Club': 'pf-diners',
      JCB: 'pf-jcb',
    };
    return Object.prototype.hasOwnProperty.call(cardBrandToPfClass, cardType.value)
      ? cardBrandToPfClass[cardType.value]
      : 'pf-credit-card';
  }
}
